<template>
  <div
    class="w-full sm:w-96 sm:flex-shrink-0 border-t sm:border-t-0 sm:border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] flex flex-col min-h-0 relative"
    @dragover.prevent
    @drop.prevent="$emit('drop')"
  >
    <Transition name="drop-hint">
      <div v-if="dragActive" class="absolute inset-3 z-10 rounded-2xl border-2 border-dashed border-red-300 dark:border-red-500/70 bg-red-50/70 dark:bg-red-900/10 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
        <div class="flex items-center gap-2 text-red-500 dark:text-red-400 font-semibold text-sm">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
          </svg>
          Drop to remove from your stack
        </div>
      </div>
    </Transition>

    <div class="flex-shrink-0 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center justify-between mb-3 gap-2">
        <div class="min-w-0">
          <h2 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Global Catalog</h2>
          <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-1 leading-none">{{ filteredComponents.length }} available</p>
        </div>
        <CategoryFilterButton icon-only :categories="availableCategories" :selected="selectedCategories" @toggle="toggleCategory" @clear="selectedCategories = []" />
      </div>

      <div class="relative">
        <svg class="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
        </svg>
        <input v-model="search" type="text" placeholder="Search by name or category"
          class="w-full pl-8 pr-7 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#0d1117] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow" />
        <button v-if="search" type="button" @click="search = ''" title="Clear search"
          class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto px-2 py-2">
      <div v-if="loading" class="animate-pulse space-y-1 px-3">
        <div v-for="i in 10" :key="i" class="flex items-center gap-3 py-2.5">
          <div class="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0"></div>
          <div class="flex-1 space-y-1.5">
            <div class="h-3 w-2/3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="flex items-center gap-1.5">
              <div class="h-2.5 w-1/4 bg-gray-100 dark:bg-gray-800 rounded"></div>
              <div class="h-3 w-12 bg-gray-100 dark:bg-gray-800 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="components.length === 0" class="flex flex-col items-center justify-center text-center py-16 px-4">
        <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
          <svg class="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
        </div>
        <p class="text-sm font-semibold text-gray-900 dark:text-white mb-1">{{ search ? `No results for "${search}"` : 'Nothing left to add' }}</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 max-w-[15rem] mb-4">{{ search ? "It's not in the catalog yet — you can add it yourself." : 'Everything in the catalog has been added to your stack.' }}</p>
        <button type="button" @click="$emit('add-custom', search)"
          class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors shadow-sm">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
          </svg>
          Add Component
        </button>
      </div>

      <!-- Everything matching the search is filtered out by category, not the
           catalog itself being empty — distinct from the branch above so the
           user isn't told to "Add Component" when the fix is really to clear
           a filter (mirrors OrgComponentsPanel's own active-filters empty state). -->
      <div v-else-if="filteredComponents.length === 0" class="flex flex-col items-center justify-center text-center py-16 px-4">
        <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
          <svg class="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
        </div>
        <p class="text-sm font-semibold text-gray-900 dark:text-white mb-1">No matching components</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 max-w-[15rem] mb-4">Try a different search term or category.</p>
        <button type="button" @click="selectedCategories = []"
          class="px-3 py-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-md transition-colors">
          Clear category filter
        </button>
      </div>

      <TransitionGroup v-else name="cat-row" tag="div">
        <div v-for="component in filteredComponents" :key="component.id"
          draggable="true"
          @dragstart="onDragStart($event, component)"
          @dragend="draggingId = null; $emit('drag-end')"
          :class="draggingId === component.id ? 'opacity-30' : ''"
          class="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-grab active:cursor-grabbing hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
          <ItComponentLogo :name="component.name" :logo-url="component.logo_url" size="w-8 h-8" />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{{ component.name }}</p>
            <div class="flex items-center gap-1.5 mt-0.5">
              <span class="text-[11px] text-gray-400 dark:text-gray-500 truncate">{{ component.vendor || 'No vendor listed' }}</span>
              <span class="inline-flex flex-shrink-0 text-[9px] font-semibold rounded-full px-1.5 py-0.5" :class="typeBadgeClass(component.type)">{{ typeLabel(component.type) }}</span>
            </div>
          </div>
          <button type="button" title="Add to organization" @click="$emit('add', component)"
            class="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-blue-600 text-white opacity-100 scale-100 sm:opacity-0 sm:scale-75 sm:group-hover:opacity-100 sm:group-hover:scale-100 transition-all duration-150 hover:bg-blue-700">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
            </svg>
          </button>
        </div>
      </TransitionGroup>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import ItComponentLogo from 'controllers/vue_components/organizations/it_components/ItComponentLogo.vue'
import CategoryFilterButton from 'controllers/vue_components/organizations/it_components/CategoryFilterButton.vue'
import { TYPE_OPTIONS, typeLabel, typeBadgeClass } from 'controllers/vue_components/organizations/it_components/itComponentOptions.js'

const props = defineProps({
  components: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  dragActive: { type: Boolean, default: false },
})

const search = defineModel('search', { type: String, default: '' })

const emit = defineEmits(['add', 'drop', 'drag-start', 'drag-end', 'add-custom'])

const draggingId = ref(null)

// Replaces the plain header icon this panel used to show — same searchable
// Category dropdown as OrgComponentsPanel's left-hand panel, but scoped to
// this org's not-yet-added catalog entries rather than its own stack, and
// filtered entirely client-side (this panel already renders off a fully
// fetched, unpaginated `components` prop, unlike the org panel's server-side
// paginated store).
const selectedCategories = ref([])

// Counts reflect the incoming (search + already-added-exclusion) list before
// the category filter itself narrows it further — same "what's the universe
// I'm picking a category out of" semantics as OrgComponentsPanel's own
// availableCategories, kept in TYPE_OPTIONS' order so the list doesn't
// reshuffle by count as the catalog/search changes.
const availableCategories = computed(() => {
  const counts = {}
  props.components.forEach((c) => { counts[c.type] = (counts[c.type] || 0) + 1 })
  return TYPE_OPTIONS
    .filter((option) => counts[option.value])
    .map((option) => ({ value: option.value, label: option.label, count: counts[option.value] }))
})

const filteredComponents = computed(() => {
  if (!selectedCategories.value.length) return props.components
  return props.components.filter((c) => selectedCategories.value.includes(c.type))
})

function toggleCategory(value) {
  const index = selectedCategories.value.indexOf(value)
  if (index === -1) selectedCategories.value = [...selectedCategories.value, value]
  else selectedCategories.value = selectedCategories.value.filter((v) => v !== value)
}

function onDragStart(event, component) {
  event.dataTransfer.effectAllowed = 'copy'
  event.dataTransfer.setData('text/plain', component.name)
  draggingId.value = component.id
  emit('drag-start', component)
}
</script>

<style scoped>
.cat-row-enter-active,
.cat-row-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.cat-row-enter-from {
  opacity: 0;
  transform: translateX(8px);
}
.cat-row-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
/* Without this, the rows below a removed one snap straight to their new spot
   the instant it's gone — right after its own fade-out finishes, which reads
   as a second, separate "jump" animation. This smooths that reflow instead. */
.cat-row-move {
  transition: transform 0.25s ease;
}

.drop-hint-enter-active,
.drop-hint-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.drop-hint-enter-from,
.drop-hint-leave-to {
  opacity: 0;
  transform: scale(0.97);
}
</style>
